import { Children, forwardRef, useCallback } from "react";
import { DisclosureContext } from "../../disclosure";
import { FocusTarget, Keys, augmentElement, isNil, mergeProps, resolveChildren, useChainedEventCallback, useEventCallback, useId, useRefState } from "../../shared";
import { MenuTriggerContext } from "./MenuTriggerContext";
import { Overlay, usePopup } from "../../overlay";
import { useInputGroupMenuAddonProps } from "../../input-group";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "div";
export function InnerMenuTrigger(props) {
  var _trigger$props$ariaL;

  const [inputGroupProps] = useInputGroupMenuAddonProps();
  const {
    align = "start",
    allowFlip,
    allowPreventOverflow,
    as = DefaultElement,
    children,
    closeOnSelect = true,
    defaultOpen,
    direction = "bottom",
    disabled,
    forwardedRef,
    id,
    onOpenChange,
    open: openProp,
    readOnly,
    zIndex = 10000,
    ...rest
  } = mergeProps(props, inputGroupProps);
  const [focusTargetRef, setFocusTarget] = useRefState(FocusTarget.first);
  const handleOpenChange = useChainedEventCallback(onOpenChange, (event, isVisible) => {
    // When the menu is closed because of a blur or outside click event, reset the focus target.
    if (!isVisible) {
      setFocusTarget(FocusTarget.first);
    }
  });
  const [trigger, menu] = Children.toArray(resolveChildren(children));

  if (isNil(trigger) || isNil(menu)) {
    throw new Error("A menu trigger must have exactly 2 children.");
  }

  const {
    isOpen,
    overlayProps,
    setIsOpen,
    triggerProps
  } = usePopup("menu", {
    allowFlip,
    allowPreventOverflow,
    defaultOpen,
    disabled: disabled || readOnly || trigger.props.disabled,
    hideOnEscape: true,
    hideOnLeave: true,
    hideOnOutsideClick: true,
    id,
    offset: [0, 4],
    onOpenChange: handleOpenChange,
    open: openProp,
    position: `${direction}-${align}`,
    restoreFocus: true,
    trigger: "click"
  });
  const open = useCallback((event, focusTarget) => {
    setFocusTarget(focusTarget);
    setIsOpen(event, true);
  }, [setIsOpen, setFocusTarget]);
  const close = useCallback(event => {
    setFocusTarget(null);
    setIsOpen(event, false);
  }, [setIsOpen, setFocusTarget]); // Open the menu on up & down arrow keydown.

  const handleTriggerKeyDown = useEventCallback(event => {
    switch (event.key) {
      case Keys.arrowDown:
        event.preventDefault();
        open(event, FocusTarget.first);
        break;

      case Keys.arrowUp:
        event.preventDefault();
        open(event, FocusTarget.last);
        break;
    }
  });
  const handleSelectionChange = useEventCallback(event => {
    if (closeOnSelect) {
      close(event);
    }
  });
  const triggerId = useId(trigger.props.id, "o-ui-menu-trigger");
  const triggerMarkup = augmentElement(trigger, mergeProps({
    id: triggerId,
    onKeyDown: !isOpen ? handleTriggerKeyDown : undefined
  }, triggerProps));
  const menuMarkup = augmentElement(menu, {
    "aria-describedby": trigger.props["aria-describedby"],
    "aria-labelledby": (_trigger$props$ariaL = trigger.props["aria-labelledby"]) !== null && _trigger$props$ariaL !== void 0 ? _trigger$props$ariaL : triggerId,
    // Must be conditional to isOpen otherwise it will steal the focus from the trigger when selecting
    // a value because the menu re-render before the exit animation is done.
    autoFocus: isOpen,
    defaultFocusTarget: focusTargetRef.current,
    onSelectionChange: handleSelectionChange
  });
  return /*#__PURE__*/_jsxs(MenuTriggerContext.Provider, {
    value: {
      close,
      isOpen,
      open
    },
    children: [/*#__PURE__*/_jsx(DisclosureContext.Provider, {
      value: {
        isOpen
      },
      children: triggerMarkup
    }), /*#__PURE__*/_jsx(Overlay, { ...mergeProps(rest, {
        as,
        ref: forwardedRef,
        zIndex
      }, overlayProps),
      children: menuMarkup
    })]
  });
}
InnerMenuTrigger.defaultElement = DefaultElement;
export const MenuTrigger = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerMenuTrigger, { ...props,
  forwardedRef: ref
}));